import axios from 'axios';
import log from '../../logger';
import { activeInstance } from './instance';

/**
 * What survives of Angular's `contentApiService`.
 *
 * That service was the data layer for the Angular Manage Assets screen, which SURF-4809
 * deleted once every tenant was shown to reach the React `manage-assets` plugin instead. Four
 * functions outlived it because the frame calls them, and they are all that is here.
 *
 * Nothing new belongs in this file. The React screens have their own data layers.
 */

/**
 * Where the app preview should point, and whether to wait for a snapshot first.
 *
 * The Angular version resolved with a bare string on failure rather than the usual object,
 * which reads like a bug and is not: `previewService` only ever looks at `url` and
 * `checkSnapshot`, and a string has neither, so the fallback silently meant "no snapshot
 * check". Returning the same shape either way says that out loud instead.
 */
export async function getPreviewConfig(scheme) {
  const params = {};
  if (scheme) params.previewScheme = scheme;
  try {
    const { data } = await axios.get('/content-api/getPreviewUrl', { params });
    return {
      url: `${data.previewUrl}?previewInstance=${activeInstance().id}`,
      checkSnapshot: data.checkSnapshot,
    };
  } catch (error) {
    log.debug('Failed to get previewUrl', error);
    return { url: `/preview?previewInstance=${activeInstance().id}`, checkSnapshot: false };
  }
}

/** Resolves false rather than rejecting, so a failed check never wedges the preview loop. */
export async function checkSnapshotIsComputedForInstance(instance) {
  try {
    const { data } = await axios.get('/content-api/checkSnapshotIsComputed', {
      params: { instanceId: instance },
    });
    return !!data.isComputed;
  } catch (error) {
    log.error(`Failed to check computed status of ${instance} snapshot.`, error);
    return false;
  }
}

/** One asset's metadata. The frame uses it to find the tenant's `cms-logo.png`. */
export async function getAssetMetadata(assetId) {
  const { data } = await axios.get('/content-api/getAssetMetadata', { params: { assetId } });
  return data.asset;
}

/** Builds the export server-side and returns the URL to download it from. */
export async function exportContent() {
  const { data } = await axios.get('/content-api/exportContent');
  return data.packageDownloadUrl;
}
